import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Building2, MapPin, User, Save, Loader2, Clock, CheckCircle,
  Calendar, Award, Package, AlertTriangle, ArrowRight, ClipboardList,
  FileEdit, XCircle, Info
} from 'lucide-react';
import { toast } from 'sonner';

// Tipos de Entidade conforme documento ENTRAJUDA 3º Steering 2026
const TIPOS_ENTIDADE = [
  'Associação de Pais',
  'Associação Solidariedade Social',
  'Autarquia',
  'Bibliotecas, Estab. Prisionais, Bombeiros e Hospitais',
  'Cáritas',
  'Casa do Povo',
  'Centros Sociais, Paroquiais e Comunitários',
  'Conferência Vicentina',
  'Congregações e Comunidades Religiosas',
  'Cooperativa',
  'Cruz Vermelha Portuguesa',
  'Empresa',
  'Entidade Governamental',
  'Escola',
  'Escuteiros',
  'Fundação',
  'Instituto',
  'Misericórdia',
  'Mutualista',
  'ONG/ONGD',
  'Outros',
  'Proteção dos Animais'
];
const SITUACOES_FINANCEIRAS = ['Deficitária', 'Equilibrada', 'Excedentária'];

const FIELD_LABELS = {
  'nome': 'Nome',
  'nif': 'NIF',
  'tipo_entidade': 'Tipo de Entidade',
  'distrito_id': 'Distrito',
  'concelho_id': 'Concelho',
  'morada': 'Morada',
  'codigo_postal': 'Código Postal',
  'telefone': 'Telefone',
  'telemovel': 'Telemóvel',
  'email_principal': 'Email Principal',
  'email_secundario': 'Email Secundário',
  'pessoa_contacto': 'Pessoa de Contacto',
  'contacto_cargo': 'Cargo do Contacto',
  'contacto_telefone': 'Telefone do Contacto',
  'contacto_email': 'Email do Contacto',
  'presidente_nome': 'Nome do Presidente',
  'presidente_cargo': 'Cargo do Presidente',
  'presidente_telefone': 'Telefone do Presidente',
  'presidente_email': 'Email do Presidente',
  'website': 'Website'
};

export default function PortalInstituicaoPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [instituicao, setInstituicao] = useState(null);
  const [historico, setHistorico] = useState(null);
  const [distritos, setDistritos] = useState([]);
  const [concelhos, setConcelhos] = useState([]);
  const [formData, setFormData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [pedidosPendentes, setPedidosPendentes] = useState([]);
  const [pedidosRecentes, setPedidosRecentes] = useState([]);

  useEffect(() => {
    loadData();
    loadPedidosPendentes();
  }, []);

  useEffect(() => {
    if (formData.distrito_id) {
      loadConcelhos(formData.distrito_id);
    }
  }, [formData.distrito_id]);

  const loadPedidosPendentes = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/instituicao/pedidos-pendentes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPedidosPendentes(data.pendentes || []);
        setPedidosRecentes(data.recentes || []);
      }
    } catch (error) {
      console.error('Error loading pending requests:', error);
    }
  };

  const loadData = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [instRes, histRes, distRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/instituicao`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/instituicao/historico`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/distritos`, { headers })
      ]);

      if (instRes.ok) {
        const instData = await instRes.json();
        setInstituicao(instData);
        setFormData(instData);
        
        // Load concelhos if distrito_id exists
        if (instData.distrito_id) {
          try {
            let concelhosRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/distritos/${instData.distrito_id}/concelhos`, { headers });
            if (!concelhosRes.ok) {
              concelhosRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/concelhos?distrito_id=${instData.distrito_id}`, { headers });
            }
            if (concelhosRes.ok) {
              const concelhosData = await concelhosRes.json();
              setConcelhos(concelhosData);
            }
          } catch (e) {
            console.error('Error loading concelhos:', e);
          }
        }
      } else if (instRes.status === 401) {
        navigate('/portal/login');
        return;
      }

      if (histRes.ok) {
        const histData = await histRes.json();
        setHistorico(histData);
      }

      if (distRes.ok) {
        const distData = await distRes.json();
        setDistritos(distData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadConcelhos = async (distritoId) => {
    try {
      // Try the new endpoint first, fallback to old one
      let response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/distritos/${distritoId}/concelhos`);
      if (!response.ok) {
        response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/concelhos?distrito_id=${distritoId}`);
      }
      if (response.ok) {
        const data = await response.json();
        setConcelhos(data);
      }
    } catch (error) {
      console.error('Error loading concelhos:', error);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('portal_token');
      
      // Only send changed fields
      const changedFields = {};
      for (const key of Object.keys(formData)) {
        if (formData[key] !== instituicao[key]) {
          changedFields[key] = formData[key];
        }
      }

      if (Object.keys(changedFields).length === 0) {
        toast.info('Nenhuma alteração para guardar');
        setSaving(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/instituicao`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(changedFields)
      });

      const data = await response.json();

      if (response.ok) {
        // Show appropriate message based on approval status
        if (data.requires_approval && data.pending_fields?.length > 0) {
          const pendingLabels = data.pending_fields.map(f => FIELD_LABELS[f] || f).join(', ');
          toast.success(`Alterações submetidas! Campos aguardando aprovação: ${pendingLabels}`, {
            duration: 5000
          });
        } else if (data.auto_approved_fields?.length > 0) {
          toast.success(data.message || 'Dados de contacto atualizados automaticamente!');
        } else {
          toast.success(data.message || 'Dados atualizados com sucesso!');
        }
        setHasChanges(false);
        loadData();
        loadPedidosPendentes(); // Reload pending requests
      } else {
        toast.error(data.detail || 'Erro ao guardar');
      }
    } catch (error) {
      toast.error('Erro ao guardar dados');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData(instituicao);
    setHasChanges(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="instituicao-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="portal-instituicao-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-[#0066CC]" />
            Minha Instituição
          </h1>
          <p className="text-slate-500">Gerir dados e informações da instituição</p>
        </div>
        {hasChanges && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  A guardar...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Guardar Alterações
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Pending Changes Banner */}
      {pedidosPendentes.length > 0 && (
        <Card className="border-amber-300 bg-amber-50" data-testid="pending-changes-banner">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileEdit className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-amber-800">
                  Alterações Pendentes de Aprovação
                </h4>
                <p className="text-sm text-amber-700 mt-1">
                  Tem {pedidosPendentes.length} pedido(s) de alteração aguardando validação pela equipa EA.
                </p>
                <div className="mt-3 space-y-2">
                  {pedidosPendentes.map((pedido) => (
                    <div key={pedido.id} className="flex items-center justify-between bg-white/60 rounded-lg p-2">
                      <div>
                        <p className="text-sm text-slate-700">
                          <strong>Campos:</strong>{' '}
                          {(pedido.campos_alterados || []).map(f => FIELD_LABELS[f] || f).join(', ')}
                        </p>
                        <p className="text-xs text-slate-500">
                          Submetido em {formatDate(pedido.created_at)}
                        </p>
                      </div>
                      <Badge className="bg-amber-500">Pendente</Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Approved/Rejected Changes */}
      {pedidosRecentes.length > 0 && (
        <Card className="border-blue-200 bg-blue-50/50" data-testid="recent-changes-banner">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Info className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-blue-800">
                  Alterações Recentes Processadas
                </h4>
                <div className="mt-2 space-y-1">
                  {pedidosRecentes.slice(0, 3).map((pedido) => (
                    <div key={pedido.id} className="flex items-center gap-2 text-sm">
                      {pedido.status === 'Aprovado' || pedido.status === 'Auto-Aprovado' ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-600" />
                      )}
                      <span className={pedido.status === 'Rejeitado' ? 'text-red-700' : 'text-green-700'}>
                        {(pedido.campos_alterados || []).map(f => FIELD_LABELS[f] || f).join(', ')}
                        {' - '}
                        <span className="font-medium">{pedido.status}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Campaign Alert */}
      {formData.tem_campanha_pendente && formData.campanhas_pendentes?.length > 0 && (
        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-900/20">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <ClipboardList className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-amber-800 dark:text-amber-300">
                  Campanha de Atualização de Dados
                </h4>
                <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                  Tem {formData.campanhas_pendentes.length} campanha(s) pendente(s) de preenchimento:
                </p>
                <div className="mt-3 space-y-2">
                  {formData.campanhas_pendentes.map((campanha) => (
                    <div key={campanha.id} className="flex items-center justify-between bg-white/60 dark:bg-slate-800/60 rounded-lg p-2">
                      <div>
                        <p className="font-medium text-sm text-slate-900 dark:text-white">
                          {campanha.nome}
                        </p>
                        {campanha.data_limite && (
                          <p className="text-xs text-slate-500">
                            Prazo: {new Date(campanha.data_limite).toLocaleDateString('pt-PT')}
                          </p>
                        )}
                      </div>
                      <Button 
                        size="sm" 
                        className="bg-amber-600 hover:bg-amber-700 h-8"
                        onClick={() => navigate(`/portal/atualizacao/${campanha.id}`)}
                      >
                        <ArrowRight className="w-4 h-4 mr-1" />
                        Preencher
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="dados" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="dados">Dados Básicos</TabsTrigger>
          <TabsTrigger value="caracterizacao">Caracterização</TabsTrigger>
          <TabsTrigger value="historico">Histórico</TabsTrigger>
        </TabsList>

        {/* Tab Dados Básicos */}
        <TabsContent value="dados" className="space-y-6">
          {/* Identificação */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#0066CC]" />
                Identificação
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome da Instituição</Label>
                  <Input value={formData.nome || ''} disabled className="bg-slate-50" />
                  <p className="text-xs text-slate-500">Contacte a EA para alterar</p>
                </div>
                <div className="space-y-2">
                  <Label>NIF</Label>
                  <Input value={formData.nif || ''} disabled className="bg-slate-50" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de Entidade</Label>
                  <Select value={formData.tipo_entidade || ''} onValueChange={(v) => updateField('tipo_entidade', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOS_ENTIDADE.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Ano Início Atividade</Label>
                  <Input
                    type="number"
                    value={formData.ano_inicio_atividade || ''}
                    onChange={(e) => updateField('ano_inicio_atividade', e.target.value ? parseInt(e.target.value) : null)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input
                    type="url"
                    value={formData.website || ''}
                    onChange={(e) => updateField('website', e.target.value)}
                    placeholder="https://..."
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Localização */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#0066CC]" />
                Localização
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Distrito</Label>
                  <Select value={formData.distrito_id || ''} onValueChange={(v) => updateField('distrito_id', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {distritos.map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Concelho {distritos.find(d => d.id === formData.distrito_id)?.nome === 'Internacional' ? '(opcional)' : ''}</Label>
                  <Select 
                    value={formData.concelho_id || ''} 
                    onValueChange={(v) => updateField('concelho_id', v)}
                    disabled={distritos.find(d => d.id === formData.distrito_id)?.nome === 'Internacional'}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={distritos.find(d => d.id === formData.distrito_id)?.nome === 'Internacional' ? 'N/A (Internacional)' : 'Selecione'} />
                    </SelectTrigger>
                    <SelectContent>
                      {concelhos.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Freguesia {distritos.find(d => d.id === formData.distrito_id)?.nome === 'Internacional' ? '(opcional)' : ''}</Label>
                  <Input
                    value={formData.freguesia || ''}
                    onChange={(e) => updateField('freguesia', e.target.value)}
                    placeholder={distritos.find(d => d.id === formData.distrito_id)?.nome === 'Internacional' ? 'N/A (Internacional)' : ''}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Morada Completa</Label>
                  <Input
                    value={formData.morada || ''}
                    onChange={(e) => updateField('morada', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Código Postal</Label>
                  <Input
                    value={formData.codigo_postal || ''}
                    onChange={(e) => updateField('codigo_postal', e.target.value)}
                    placeholder="1234-567"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contactos */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-[#0066CC]" />
                Contactos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Telefone Principal</Label>
                  <Input
                    value={formData.telefone || ''}
                    onChange={(e) => updateField('telefone', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telemóvel</Label>
                  <Input
                    value={formData.telemovel || ''}
                    onChange={(e) => updateField('telemovel', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fax</Label>
                  <Input
                    value={formData.fax || ''}
                    onChange={(e) => updateField('fax', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email Principal</Label>
                  <Input
                    type="email"
                    value={formData.email_principal || ''}
                    onChange={(e) => updateField('email_principal', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email Secundário</Label>
                  <Input
                    type="email"
                    value={formData.email_secundario || ''}
                    onChange={(e) => updateField('email_secundario', e.target.value)}
                  />
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-4">Pessoa de Contacto</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nome</Label>
                    <Input
                      value={formData.pessoa_contacto || ''}
                      onChange={(e) => updateField('pessoa_contacto', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cargo</Label>
                    <Input
                      value={formData.contacto_cargo || ''}
                      onChange={(e) => updateField('contacto_cargo', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Telefone</Label>
                    <Input
                      value={formData.contacto_telefone || ''}
                      onChange={(e) => updateField('contacto_telefone', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.contacto_email || ''}
                      onChange={(e) => updateField('contacto_email', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Presidente/Representante Legal */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-green-600" />
                Presidente / Representante Legal
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input
                    value={formData.presidente_nome || ''}
                    onChange={(e) => updateField('presidente_nome', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cargo</Label>
                  <Input
                    value={formData.presidente_cargo || ''}
                    onChange={(e) => updateField('presidente_cargo', e.target.value)}
                    placeholder="Ex: Presidente, Provedor"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefone</Label>
                  <Input
                    value={formData.presidente_telefone || ''}
                    onChange={(e) => updateField('presidente_telefone', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.presidente_email || ''}
                    onChange={(e) => updateField('presidente_email', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recursos Humanos */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recursos Humanos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Nº Técnicos Remunerados</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.num_tecnicos || ''}
                    onChange={(e) => updateField('num_tecnicos', e.target.value ? parseInt(e.target.value) : 0)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nº Outros Colaboradores</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.num_outros_colaboradores || ''}
                    onChange={(e) => updateField('num_outros_colaboradores', e.target.value ? parseInt(e.target.value) : 0)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nº Voluntários Regulares</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.num_voluntarios || ''}
                    onChange={(e) => updateField('num_voluntarios', e.target.value ? parseInt(e.target.value) : 0)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Observações</Label>
                <Textarea
                  value={formData.observacoes_rh || ''}
                  onChange={(e) => updateField('observacoes_rh', e.target.value)}
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Caracterização */}
        <TabsContent value="caracterizacao" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Caracterização da Instituição</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Missão</Label>
                <Textarea
                  value={formData.missao || ''}
                  onChange={(e) => updateField('missao', e.target.value)}
                  placeholder="Descreva a missão da instituição..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>População Apoiada</Label>
                <Textarea
                  value={formData.populacao_apoiada || ''}
                  onChange={(e) => updateField('populacao_apoiada', e.target.value)}
                  placeholder="Descreva a população que apoiam..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Área Geográfica de Atuação</Label>
                <Input
                  value={formData.area_geografica || ''}
                  onChange={(e) => updateField('area_geografica', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>Situação Financeira</Label>
                <Select value={formData.situacao_financeira || ''} onValueChange={(v) => updateField('situacao_financeira', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {SITUACOES_FINANCEIRAS.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Parcerias Ativas</Label>
                <Textarea
                  value={formData.parcerias || ''}
                  onChange={(e) => updateField('parcerias', e.target.value)}
                  placeholder="Ex: Segurança Social, Câmara Municipal, Banco Alimentar..."
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Histórico */}
        <TabsContent value="historico" className="space-y-6">
          {historico && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Data Registo EA</p>
                        <p className="font-semibold">
                          {historico.data_registo 
                            ? new Date(historico.data_registo).toLocaleDateString('pt-PT')
                            : '-'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <Award className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Código EA</p>
                        <p className="font-semibold">{historico.codigo_ea || '-'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Package className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Total Apoios Recebidos</p>
                        <p className="font-semibold">{historico.num_apoios || 0} apoios</p>
                        {historico.total_apoios_recebidos > 0 && (
                          <p className="text-xs text-slate-400">
                            €{historico.total_apoios_recebidos.toLocaleString('pt-PT')} valor estimado
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {historico.ultima_avaliacao && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Última Avaliação</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#0066CC] to-[#004499] flex items-center justify-center">
                        <span className="text-2xl font-bold text-white">
                          {historico.ultima_avaliacao.score_global?.toFixed(1) || '-'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">Ano {historico.ultima_avaliacao.ano_referencia}</p>
                        <Badge className={
                          historico.ultima_avaliacao.status === 'Finalizada' 
                            ? 'bg-green-500' 
                            : 'bg-amber-500'
                        }>
                          {historico.ultima_avaliacao.status}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {historico.avaliacoes && historico.avaliacoes.length > 1 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Histórico de Avaliações</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {historico.avaliacoes.map((av, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <span>Ano {av.ano_referencia}</span>
                          <div className="flex items-center gap-3">
                            <span className="font-semibold">{av.score_global?.toFixed(1) || '-'}</span>
                            <Badge variant="outline">{av.status}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Sticky Save Bar */}
      {hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 lg:left-72 bg-white border-t p-4 shadow-lg z-40">
          <div className="container max-w-4xl mx-auto flex items-center justify-between">
            <p className="text-sm text-slate-600">
              <Clock className="w-4 h-4 inline mr-1" />
              Tem alterações não guardadas
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancel}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Guardar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
